import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Clock,
  CreditCard,
  DollarSign,
  TrendingUp,
  Wallet,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { apiGet } from "@/services/api";
import { formatPrice } from "@/lib/utils";

type Earnings = {
  grossSales: number;
  platformCommission: number;
  refunds: number;
  withdrawals: number;
  netEarnings: number;
  availableBalance: number;
  pendingBalance: number;
};

type Txn = {
  id: string;
  type: string;
  grossAmount: number;
  commissionAmount: number;
  netAmount: number;
  status: string;
  reference: string | null;
  createdAt: string;
};

export function VendorEarningsPage() {
  const [earnings, setEarnings] = useState<Earnings | null>(null);
  const [txns, setTxns] = useState<Txn[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      apiGet<Earnings>("/api/vendors/me/earnings")
        .then((d) => setEarnings(d))
        .catch(() => {}),
      apiGet<{ transactions: Txn[] }>("/api/vendors/me/transactions")
        .then((d) => setTxns(d?.transactions ?? []))
        .catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white">
            Earnings & Revenue
          </h1>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            Monitor gross sales, platform commission deductions, and net payouts.
          </p>
        </div>

        <Link to="/vendor/withdrawals" className="w-full sm:w-auto">
          <button
            type="button"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 rounded-xl bg-[#087DB5] hover:bg-[#073B70] px-3.5 py-2 text-xs font-bold text-white shadow-xs transition active:scale-95 cursor-pointer"
          >
            <Wallet className="h-3.5 w-3.5" />
            <span>Request Payout</span>
          </button>
        </Link>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-2 gap-2.5 sm:gap-4 lg:grid-cols-4">
        <StatCard
          label="Gross sales"
          value={formatPrice(earnings?.grossSales ?? 0)}
          icon={DollarSign}
          iconColor="text-blue-600 bg-blue-50 dark:bg-blue-950/60 dark:text-blue-400"
        />
        <StatCard
          label="Net earnings"
          value={formatPrice(earnings?.netEarnings ?? 0)}
          icon={TrendingUp}
          iconColor="text-emerald-600 bg-emerald-50 dark:bg-emerald-950/60 dark:text-emerald-400"
          highlight
        />
        <StatCard
          label="Available"
          value={formatPrice(earnings?.availableBalance ?? 0)}
          icon={Wallet}
          iconColor="text-teal-600 bg-teal-50 dark:bg-teal-950/60 dark:text-teal-400"
        />
        <StatCard
          label="Pending payout"
          value={formatPrice(earnings?.pendingBalance ?? 0)}
          icon={Clock}
          iconColor="text-orange-600 bg-orange-50 dark:bg-orange-950/60 dark:text-orange-400"
        />
        <StatCard
          label="Commission"
          value={formatPrice(earnings?.platformCommission ?? 0)}
          icon={CreditCard}
          iconColor="text-amber-600 bg-amber-50 dark:bg-amber-950/60 dark:text-amber-400"
        />
        <StatCard
          label="Refunds"
          value={formatPrice(earnings?.refunds ?? 0)}
          icon={ArrowUpRight}
          iconColor="text-rose-600 bg-rose-50 dark:bg-rose-950/60 dark:text-rose-400"
        />
        <StatCard
          label="Withdrawn"
          value={formatPrice(earnings?.withdrawals ?? 0)}
          icon={Wallet}
          iconColor="text-slate-600 bg-slate-100 dark:bg-slate-800 dark:text-slate-300"
        />
      </div>

      {/* Transactions History */}
      <div className="amazon-card p-4 sm:p-5 overflow-hidden">
        <div className="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-white/10">
          <h3 className="font-bold text-sm sm:text-base text-slate-900 dark:text-white">
            Transactions History
          </h3>
          <span className="text-xs text-slate-500">
            {txns.length} record{txns.length !== 1 ? "s" : ""}
          </span>
        </div>

        {/* MOBILE TRANSACTIONS VIEW (< sm) */}
        <div className="block sm:hidden divide-y divide-slate-100 dark:divide-white/5 mt-2">
          {txns.map((t) => (
            <div key={t.id} className="py-3 space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-slate-500">
                  {new Date(t.createdAt).toLocaleDateString()}
                </span>
                <Badge tone={t.status === "COMPLETED" ? "success" : "prime"}>
                  {t.status}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                  {t.type}
                </span>
                <span className="text-sm font-black text-emerald-600 dark:text-emerald-400">
                  {formatPrice(t.netAmount)}
                </span>
              </div>
              <div className="flex items-center justify-between text-[11px] text-slate-400">
                <span>Gross: {formatPrice(t.grossAmount)}</span>
                <span>Fee: -{formatPrice(t.commissionAmount)}</span>
              </div>
            </div>
          ))}
          {txns.length === 0 && !loading && (
            <p className="py-6 text-center text-xs text-slate-500">No transactions recorded yet.</p>
          )}
        </div>

        {/* DESKTOP TRANSACTIONS TABLE (>= sm) */}
        <div className="hidden sm:block overflow-x-auto">
          <table className="mt-3 w-full min-w-[500px] text-sm">
            <thead>
              <tr className="text-left text-xs uppercase text-slate-500 border-b border-slate-100 dark:border-white/10 pb-2">
                <th className="py-2">Date</th>
                <th>Type</th>
                <th>Gross</th>
                <th>Commission</th>
                <th>Net Earnings</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-white/10">
              {txns.map((t) => (
                <tr key={t.id} className="hover:bg-slate-50/50 dark:hover:bg-white/[0.02]">
                  <td className="py-2.5 text-xs text-slate-500">
                    {new Date(t.createdAt).toLocaleDateString()}
                  </td>
                  <td className="font-semibold text-slate-800 dark:text-slate-200">{t.type}</td>
                  <td>{formatPrice(t.grossAmount)}</td>
                  <td className="text-rose-600 dark:text-rose-400">
                    -{formatPrice(t.commissionAmount)}
                  </td>
                  <td className="font-bold text-emerald-600 dark:text-emerald-400">
                    {formatPrice(t.netAmount)}
                  </td>
                  <td>
                    <Badge tone={t.status === "COMPLETED" ? "success" : "prime"}>
                      {t.status}
                    </Badge>
                  </td>
                </tr>
              ))}
              {txns.length === 0 && !loading && (
                <tr>
                  <td colSpan={6} className="py-6 text-center text-slate-500">
                    No transactions recorded yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function StatCard({
  label,
  value,
  icon: Icon,
  iconColor,
  highlight,
}: {
  label: string;
  value: string;
  icon: React.ElementType;
  iconColor: string;
  highlight?: boolean;
}) {
  return (
    <div
      className={`amazon-card p-3 sm:p-4 flex flex-col justify-between transition-all ${
        highlight ? "ring-1 ring-emerald-500/30" : ""
      }`}
    >
      <div className="flex items-center justify-between gap-1 mb-2">
        <p className="text-[10px] sm:text-xs font-bold uppercase text-slate-500 dark:text-slate-400 truncate">
          {label}
        </p>
        <div className={`flex h-6 w-6 sm:h-7 sm:w-7 shrink-0 items-center justify-center rounded-lg ${iconColor}`}>
          <Icon className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
        </div>
      </div>
      <p
        className={`text-sm sm:text-xl font-black truncate tracking-tight ${
          highlight ? "text-emerald-600 dark:text-emerald-400" : "text-slate-900 dark:text-white"
        }`}
        title={value}
      >
        {value}
      </p>
    </div>
  );
}

